import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Select } from 'components/Shared';
import type { SelectOption } from 'contracts/Common';
import type { DockCarrier } from 'contracts/Docks';
import { useValidation } from 'hooks';
import { Formatter } from 'utils';
import {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import {
  CreateDockCarrierActions as CreateActions,
  DeleteDockCarrierActions as DeleteActions,
} from 'store/ducks/dockCarriers';
import * as Yup from 'yup';
import * as S from './styles';

export interface Ref {
  resetForm: () => void;
}

interface Props {
  dockId?: number;
  dockCarriers: DockCarrier[];
  onUpdate?: () => void;
}

interface ICarriersManager
  extends ForwardRefExoticComponent<Props & RefAttributes<Ref>> {}

interface ItemProps {
  dockCarrier: DockCarrier;
}

const CarriersManager: ICarriersManager = forwardRef<Ref, Props>(
  (props, ref) => {
    const { dockId, dockCarriers, onUpdate } = props;

    const dispatch: AppDispatch = useDispatch();
    const formRef = useRef<FormHandles>(null);

    const { handleFormErrors, handleApiErrors } = useValidation();
    const [carrierOptions, setCarrierOptions] = useState<SelectOption[]>([]);

    /**
     * this component will not fetch carriers here,
     * we lifted it to a upper level (DocksManager component)
     * to avoid dispatching multiple times
     * */
    const { loading: loadingCarriers, data: carriers } = useSelector(
      (state: RootState) => state.listCarriers
    );

    const { loading: creating, validationErrors } = useSelector(
      (state: RootState) => state.createDockCarrier
    );

    const { id: deletingId } = useSelector(
      (state: RootState) => state.deleteDockCarrier
    );

    /**
     * we will also filter the list of carriers to only show
     * the ones that are not already bound to the dock
     * */
    const onCarrriersLoad = useCallback((): void => {
      setCarrierOptions(
        carriers
          .filter((carrier) => {
            return !dockCarriers.find(
              (dockCarrier) => dockCarrier.carrier.id === carrier.id
            );
          })
          .map((carrier) => ({
            value: carrier.id,
            label: `${carrier.tradeName} - ${Formatter.document(
              carrier.document,
              carrier.documentType
            )}`,
          }))
      );
    }, [carriers, dockCarriers]);

    const onDelete = useCallback(
      (id: number): void => {
        dispatch(DeleteActions.request(id, onUpdate));
      },
      [dispatch, onUpdate]
    );

    const onSuccess = useCallback((): void => {
      onUpdate && onUpdate();
      formRef.current?.reset();
    }, [onUpdate]);

    const onSubmit = useCallback(
      async (data: any): Promise<void> => {
        try {
          formRef.current?.setErrors({});

          const schema = Yup.object().shape({
            carrierId: Yup.number()
              .typeError('Selecione a transportadora')
              .required('Selecione a transportadora'),
          });

          const validData = await schema.validate(data, {
            abortEarly: false,
          });

          dispatch(CreateActions.request({ ...validData, dockId }, onSuccess));
        } catch (error) {
          handleFormErrors(error, formRef);
        }
      },
      [dispatch, dockId, handleFormErrors, onSuccess]
    );

    const Item = useCallback(
      (props: ItemProps): JSX.Element => {
        const { id, carrier } = props.dockCarrier;

        return (
          <S.ListItem>
            <S.Column>{carrier.tradeName}</S.Column>
            <S.ActionsColumn>
              <S.ActionButton
                disabled={deletingId === id}
                onClick={() => onDelete(id)}
                mood="danger"
              >
                {deletingId === id ? <S.ActivityIndicator /> : <S.TrashIcon />}
              </S.ActionButton>
            </S.ActionsColumn>
          </S.ListItem>
        );
      },
      [deletingId, onDelete]
    );

    useEffect(() => {
      onCarrriersLoad();
    }, [onCarrriersLoad]);

    useEffect(() => {
      handleApiErrors(validationErrors, formRef);
    }, [handleApiErrors, validationErrors]);

    useImperativeHandle(
      ref,
      () => ({
        resetForm: () => {
          formRef.current?.reset();
          formRef.current?.setErrors({});
        },
      }),
      []
    );

    return (
      <S.Container>
        <Form ref={formRef} onSubmit={onSubmit}>
          <S.FormRow>
            <Select
              name="carrierId"
              label="Transportadora"
              options={carrierOptions}
              isLoading={loadingCarriers}
              menuPortalTarget={document.body}
            />
            <S.Button type="submit">
              {creating ? <S.ActivityIndicator /> : 'Adicionar'}
            </S.Button>
          </S.FormRow>
        </Form>
        {dockCarriers.map((dockCarrier) => (
          <Item dockCarrier={dockCarrier} key={dockCarrier.id} />
        ))}
      </S.Container>
    );
  }
);

export default CarriersManager;
